/**
 * Инструменты на основе объёма: VWAP с привязкой и профили объёма.
 *
 * Три инструмента, один источник — свечи графика:
 *
 * - `anchored-vwap` — средняя цена, взвешенная по объёму, от выбранной
 *   свечи до последней, с полосами отклонения;
 * - `volume-profile-range` — сколько объёма прошло на каждой цене между
 *   двумя моментами времени;
 * - `volume-profile-anchored` — то же от выбранной свечи до последней,
 *   гистограмма у правого края графика.
 *
 * Здесь только счёт и настройки, без холста: считают одни и те же
 * функции и рисование, и попадание курсором, и тесты.
 *
 * Объём свечи раскладывается по её диапазону от минимума до максимума
 * равномерно. Сделок внутри свечи у графика нет, поэтому профиль тем
 * точнее, чем мельче таймфрейм; растущий и падающий объём — по тому,
 * закрылась свеча выше открытия или ниже.
 */

import type { Candle } from '../state/store.js';
import type { DrawingKind } from './kinds.js';

export const VOLUME_KINDS = new Set<DrawingKind>(['anchored-vwap', 'volume-profile-range', 'volume-profile-anchored']);
export const PROFILE_KINDS = new Set<DrawingKind>(['volume-profile-range', 'volume-profile-anchored']);

export function isVolumeKind(kind: DrawingKind): boolean {
  return VOLUME_KINDS.has(kind);
}

export function isProfileKind(kind: DrawingKind): boolean {
  return PROFILE_KINDS.has(kind);
}

/* ------------------------------------------------------------ настройки */

export type VwapSource = 'open' | 'high' | 'low' | 'close' | 'hl2' | 'hlc3' | 'ohlc4' | 'hlcc4';

export const VWAP_SOURCES: ReadonlyArray<{ id: VwapSource; name: string }> = [
  { id: 'hlc3', name: '(Макс + Мин + Закр) / 3' },
  { id: 'close', name: 'Цена закрытия' },
  { id: 'open', name: 'Цена открытия' },
  { id: 'high', name: 'Максимум' },
  { id: 'low', name: 'Минимум' },
  { id: 'hl2', name: '(Макс + Мин) / 2' },
  { id: 'ohlc4', name: '(Откр + Макс + Мин + Закр) / 4' },
  { id: 'hlcc4', name: '(Макс + Мин + Закр + Закр) / 4' },
];

export type BandMode = 'stdev' | 'percent';
export type RowsMode = 'count' | 'ticks';
export type ProfileMode = 'updown' | 'total' | 'delta';
export type ProfileSide = 'left' | 'right';

export interface VwapBand {
  readonly on: boolean;
  readonly multiplier: number;
}

export interface VolumeOptions {
  /* --- VWAP */
  readonly source: VwapSource;
  /** Полосы — в стандартных отклонениях или в процентах от линии. */
  readonly bandMode: BandMode;
  readonly bands: readonly [VwapBand, VwapBand, VwapBand];
  readonly bandColor: string;
  /** Заливка между полосами. */
  readonly bandFill: boolean;
  /** Цена линии у правого края. */
  readonly priceTag: boolean;

  /* --- профиль */
  /** Высота строки — числом строк на весь диапазон или тиками на строку. */
  readonly rowsMode: RowsMode;
  readonly rowSize: number;
  /** Что показывают строки: рост и падение, сумму или разницу. */
  readonly mode: ProfileMode;
  /** Зона стоимости, % объёма вокруг самой объёмной цены. */
  readonly valueArea: number;
  /** Продолжить уровни вправо, за конец диапазона. */
  readonly extendRight: boolean;
  /** Ширина гистограммы, % от ширины диапазона. */
  readonly width: number;
  readonly side: ProfileSide;
  readonly upColor: string;
  readonly downColor: string;
  readonly poc: boolean;
  readonly pocColor: string;
  /** Границы зоны стоимости — VAH и VAL. */
  readonly valueLines: boolean;
  /** Объём строки числом. */
  readonly values: boolean;
  /** Подложка диапазона. */
  readonly box: boolean;
}

const COMMON: VolumeOptions = {
  source: 'hlc3',
  bandMode: 'stdev',
  bands: [
    { on: true, multiplier: 1 },
    { on: false, multiplier: 2 },
    { on: false, multiplier: 3 },
  ],
  bandColor: '#2EE6B0',
  bandFill: true,
  priceTag: true,
  rowsMode: 'count',
  rowSize: 24,
  mode: 'updown',
  valueArea: 70,
  extendRight: false,
  width: 30,
  side: 'left',
  upColor: '#2EE6B0',
  downColor: '#FF5470',
  poc: true,
  pocColor: '#F5C04E',
  valueLines: true,
  values: false,
  box: true,
};

/** Умолчания по инструменту: у профиля с привязкой гистограмма справа, у края графика. */
export function defaultVolumeOptions(kind: DrawingKind): VolumeOptions {
  return kind === 'volume-profile-anchored' ? { ...COMMON, side: 'right', box: false } : COMMON;
}

export function volumeOptionsOf(kind: DrawingKind, stored: Partial<VolumeOptions> | undefined): VolumeOptions {
  const base = defaultVolumeOptions(kind);
  return stored ? { ...base, ...stored } : base;
}

const HEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;
const within = (value: unknown, min: number, max: number, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : fallback;

/** Приведение сохранённого: чужие и битые значения заменяются умолчаниями инструмента. */
export function normalizeVolumeOptions(kind: DrawingKind, value: unknown): VolumeOptions | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Partial<VolumeOptions>;
  const base = defaultVolumeOptions(kind);
  const color = (entry: unknown, fallback: string): string => (typeof entry === 'string' && HEX.test(entry) ? entry : fallback);
  const band = (entry: unknown, fallback: VwapBand): VwapBand => {
    const item = (typeof entry === 'object' && entry !== null ? entry : {}) as Partial<VwapBand>;
    return { on: typeof item.on === 'boolean' ? item.on : fallback.on, multiplier: within(item.multiplier, 0.1, 20, fallback.multiplier) };
  };
  const bands = Array.isArray(raw.bands) ? raw.bands : [];
  return {
    source: VWAP_SOURCES.some((entry) => entry.id === raw.source) ? raw.source! : base.source,
    bandMode: raw.bandMode === 'percent' ? 'percent' : 'stdev',
    bands: [band(bands[0], base.bands[0]), band(bands[1], base.bands[1]), band(bands[2], base.bands[2])],
    bandColor: color(raw.bandColor, base.bandColor),
    bandFill: raw.bandFill !== false,
    priceTag: raw.priceTag !== false,
    rowsMode: raw.rowsMode === 'ticks' ? 'ticks' : 'count',
    rowSize: Math.round(within(raw.rowSize, 1, 1_000_000, base.rowSize)),
    mode: raw.mode === 'total' || raw.mode === 'delta' ? raw.mode : 'updown',
    valueArea: within(raw.valueArea, 10, 100, base.valueArea),
    extendRight: raw.extendRight === true,
    width: within(raw.width, 5, 100, base.width),
    side: raw.side === 'left' || raw.side === 'right' ? raw.side : base.side,
    upColor: color(raw.upColor, base.upColor),
    downColor: color(raw.downColor, base.downColor),
    poc: raw.poc !== false,
    pocColor: color(raw.pocColor, base.pocColor),
    valueLines: raw.valueLines !== false,
    values: raw.values === true,
    box: typeof raw.box === 'boolean' ? raw.box : base.box,
  };
}

/* ------------------------------------------------------------ VWAP */

export function sourceOf(candle: Candle, source: VwapSource): number {
  switch (source) {
    case 'open':
      return candle.open;
    case 'high':
      return candle.high;
    case 'low':
      return candle.low;
    case 'close':
      return candle.close;
    case 'hl2':
      return (candle.high + candle.low) / 2;
    case 'ohlc4':
      return (candle.open + candle.high + candle.low + candle.close) / 4;
    case 'hlcc4':
      return (candle.high + candle.low + candle.close * 2) / 4;
    default:
      return (candle.high + candle.low + candle.close) / 3;
  }
}

export interface VwapPoint {
  readonly time: number;
  readonly value: number;
  /** Стандартное отклонение цены от линии к этой свече, взвешенное объёмом. */
  readonly deviation: number;
}

/** Свечи от привязки включительно: привязка между свечами берёт следующую. */
function fromAnchor(candles: readonly Candle[], anchor: number): number {
  let low = 0;
  let high = candles.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (candles[mid]!.time < anchor) low = mid + 1;
    else high = mid;
  }
  return low;
}

/**
 * VWAP от привязки до последней свечи.
 *
 * Свечи без объёма линию не двигают: до первой сделки значение — сама
 * цена свечи, иначе линия начиналась бы с деления на ноль.
 */
export function anchoredVwap(candles: readonly Candle[], anchor: number, source: VwapSource): VwapPoint[] {
  const out: VwapPoint[] = [];
  let volume = 0;
  let priced = 0;
  let squared = 0;
  for (let i = fromAnchor(candles, anchor); i < candles.length; i += 1) {
    const candle = candles[i]!;
    const price = sourceOf(candle, source);
    const size = Number.isFinite(candle.volume) && candle.volume > 0 ? candle.volume : 0;
    volume += size;
    priced += price * size;
    squared += price * price * size;
    if (volume <= 0) {
      out.push({ time: candle.time, value: price, deviation: 0 });
      continue;
    }
    const value = priced / volume;
    out.push({ time: candle.time, value, deviation: Math.sqrt(Math.max(0, squared / volume - value * value)) });
  }
  return out;
}

/** Расстояние полосы от линии в цене: в отклонениях или в процентах от самой линии. */
export function bandOffset(point: VwapPoint, mode: BandMode, multiplier: number): number {
  return mode === 'percent' ? (point.value * multiplier) / 100 : point.deviation * multiplier;
}

/* ------------------------------------------------------------ профиль объёма */

export interface ProfileRow {
  /** Нижняя и верхняя цена строки. */
  readonly from: number;
  readonly to: number;
  /** Объём свечей, закрывшихся не ниже открытия, и остальных. */
  readonly up: number;
  readonly down: number;
}

export interface VolumeProfile {
  readonly rows: readonly ProfileRow[];
  /** Строка с наибольшим объёмом — точка контроля (POC). */
  readonly poc: number;
  /** Нижняя и верхняя строки зоны стоимости. */
  readonly valueLow: number;
  readonly valueHigh: number;
  readonly total: number;
  /** Самая большая величина строки в выбранном режиме — по ней масштаб ширины. */
  readonly peak: number;
  /** Свечи диапазона: первая и последняя. */
  readonly firstTime: number;
  readonly lastTime: number;
  readonly low: number;
  readonly high: number;
  readonly bars: number;
}

/** Больше строк, чем различит глаз, не нужно — и это предел счёта на кадр. */
export const MAX_ROWS = 400;

/** Величина строки в выбранном режиме: в «дельте» — модуль разницы. */
export function rowSizeOf(row: ProfileRow, mode: ProfileMode): number {
  return mode === 'delta' ? Math.abs(row.up - row.down) : row.up + row.down;
}

/**
 * Профиль объёма по свечам от `from` до `to` включительно.
 *
 * `null` — в диапазоне нет свечей или цена не двигалась: строить не из чего.
 */
export function volumeProfile(
  candles: readonly Candle[],
  from: number,
  to: number,
  options: Pick<VolumeOptions, 'rowsMode' | 'rowSize' | 'valueArea' | 'mode'>,
  tickSize: number,
): VolumeProfile | null {
  const [start, end] = from <= to ? [from, to] : [to, from];
  const first = fromAnchor(candles, start);
  let last = first - 1;
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (let i = first; i < candles.length && candles[i]!.time <= end; i += 1) {
    last = i;
    low = Math.min(low, candles[i]!.low);
    high = Math.max(high, candles[i]!.high);
  }
  if (last < first || !(high > low)) return null;

  const span = high - low;
  const wanted = options.rowsMode === 'ticks' && tickSize > 0 ? Math.ceil(span / (tickSize * Math.max(1, options.rowSize))) : options.rowSize;
  const count = Math.max(1, Math.min(MAX_ROWS, Math.round(wanted)));
  const step = span / count;
  const up = new Float64Array(count);
  const down = new Float64Array(count);

  for (let i = first; i <= last; i += 1) {
    const candle = candles[i]!;
    const size = Number.isFinite(candle.volume) && candle.volume > 0 ? candle.volume : 0;
    if (size === 0) continue;
    const target = candle.close >= candle.open ? up : down;
    const range = candle.high - candle.low;
    const a = Math.min(count - 1, Math.max(0, Math.floor((candle.low - low) / step)));
    const b = Math.min(count - 1, Math.max(0, Math.floor((candle.high - low) / step)));
    // Свеча-чёрточка: весь объём — в одну строку.
    if (!(range > 0) || a === b) {
      target[a]! += size;
      continue;
    }
    for (let row = a; row <= b; row += 1) {
      const overlap = Math.min(candle.high, low + step * (row + 1)) - Math.max(candle.low, low + step * row);
      if (overlap > 0) target[row]! += (size * overlap) / range;
    }
  }

  const rows: ProfileRow[] = [];
  let total = 0;
  let poc = 0;
  let peak = 0;
  for (let row = 0; row < count; row += 1) {
    const entry = { from: low + step * row, to: low + step * (row + 1), up: up[row]!, down: down[row]! };
    rows.push(entry);
    const sum = entry.up + entry.down;
    total += sum;
    if (sum > rows[poc]!.up + rows[poc]!.down) poc = row;
    peak = Math.max(peak, rowSizeOf(entry, options.mode));
  }

  // Зона стоимости: от точки контроля в стороны, каждый раз — к более объёмной соседке.
  const sumOf = (row: number): number => rows[row]!.up + rows[row]!.down;
  let valueLow = poc;
  let valueHigh = poc;
  let inside = sumOf(poc);
  const need = (total * options.valueArea) / 100;
  while (inside < need && (valueLow > 0 || valueHigh < count - 1)) {
    const below = valueLow > 0 ? sumOf(valueLow - 1) : -1;
    const above = valueHigh < count - 1 ? sumOf(valueHigh + 1) : -1;
    if (above >= below) {
      valueHigh += 1;
      inside += above;
    } else {
      valueLow -= 1;
      inside += below;
    }
  }

  return { rows, poc, valueLow, valueHigh, total, peak, firstTime: candles[first]!.time, lastTime: candles[last]!.time, low, high, bars: last - first + 1 };
}

/** Объём коротко: 1,2 тыс., 3,4 млн — у строки места на длинное число нет. */
export function volumeText(value: number): string {
  const abs = Math.abs(value);
  const short = (scaled: number, unit: string): string => `${scaled.toFixed(scaled >= 100 ? 0 : 1).replace('.', ',')} ${unit}`;
  if (abs >= 1e9) return short(value / 1e9, 'млрд');
  if (abs >= 1e6) return short(value / 1e6, 'млн');
  if (abs >= 1e3) return short(value / 1e3, 'тыс.');
  return value.toFixed(abs >= 10 ? 0 : 2).replace('.', ',');
}
